import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  FaPhoneAlt,
  FaTimes,
  FaChevronRight,
  FaArrowRight,
} from "react-icons/fa";
import Noise from "../core/noise";
import CircleIconAnimationButton from "./circle-icon-animation-button";

const navLinks = [
  {
    label: "Business Signs",
    href: "/business-signs/",
    dropdown: [
      { label: "Aluminum Signs", href: "/business-signs/aluminum-signs/" },
      { label: "Exterior Signs", href: "/business-signs/exterior-signs/" },
      { label: "Interior Signs", href: "/business-signs/interior-signs/" },
      { label: "Metal Signs", href: "/business-signs/metal-signs/" },
      { label: "Pylon Signs", href: "/business-signs/pylon-signs/" },
      {
        label: "Illuminated Signs",
        href: "/business-signs/illuminated-signs/",
      },
      { label: "Fascia Signs", href: "/business-signs/fascia-signs/" },
      { label: "Office Signs", href: "/business-signs/office-signs/" },
      { label: "Custom Signs", href: "/business-signs/custom-signs/" },
    ],
  },
  {
    label: "LED Signs",
    href: "/led-signs/",
    dropdown: [
      { label: "Indoor and Outdoor", href: "/led-signs/indoor-and-outdoor/" },
      { label: "Message Boards", href: "/led-signs/message-boards/" },
    ],
  },
  {
    label: "Wholesale Signs",
    href: "/wholesale-signs/",
    dropdown: [
      { label: "Custom Signs", href: "/wholesale-signs/custom-signs/" },
    ],
  },
  {
    label: "Sign Service Install",
    href: "/sign-service-install/",
    dropdown: null,
  },
  {
    label: "About",
    href: "/about/",
    dropdown: [
      {
        label: "Business Owners Signage Solutions",
        href: "/about/business-owners-signage-solutions/",
      },
      {
        label: "Marketing Professionals",
        href: "/about/marketing-professionals/",
      },
      {
        label: "Property Manager & Contractors",
        href: "/about/property-manager-contractors/",
      },
      { label: "Areas We Serve", href: "/about/areas-we-serve/" },
      {
        label: "Edmonton Sign & Bucket Truck Services",
        href: "/about/edmonton-sign-and-bucket-truck-services/",
      },
      {
        label: "Calgary Sign & Bucket Truck Services",
        href: "/about/calgary-sign-and-bucket-truck-services/",
      },
      {
        label: "Red Deer Sign & Bucket Truck Services",
        href: "/about/red-deer-sign-and-bucket-truck-services/",
      },
      {
        label: "St Albert Sign & Bucket Truck Services",
        href: "/about/st-albert-sign-and-bucket-truck-services/",
      },
      {
        label: "Sherwood Park Sign & Bucket Truck Services",
        href: "/about/sherwood-park-sign-and-bucket-truck-services/",
      },
    ],
  },
  {
    label: "Portfolio",
    href: "/portfolio/",
    dropdown: [
      { label: "Letter Signs", href: "/portfolio/letter-signs/" },
      { label: "Exterior Signs", href: "/portfolio/exterior-signs/" },
      {
        label: "Freestanding Pylon Signs",
        href: "/portfolio/freestanding-pylon-signs/",
      },
      { label: "Fascia Signs", href: "/portfolio/fascia-signs/" },
      { label: "Interior Signs", href: "/portfolio/interior-signs/" },
    ],
  },
  {
    label: "Bucket Truck Services",
    href: "/bucket-truck-services/",
    dropdown: null,
  },
];

const phones = [
  { label: "Toll Free", number: "+1.855.638.2539", tel: "18556382539" },
  { label: "Northern AB", number: "+1.780.975.2558", tel: "17809752558" },
  { label: "Southern AB", number: "+1.403.437.2600", tel: "14034372600" },
];

const HamburgerIcon = ({ open }: { open: boolean }) => (
  <div className="w-5 h-3.5 flex flex-col justify-between relative">
    <motion.span
      animate={open ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
      transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
      className="block h-px bg-white origin-left"
    />
    <motion.span
      animate={open ? { opacity: 0, x: 8 } : { opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
      className="block h-px bg-white/70"
    />
    <motion.span
      animate={open ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
      transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
      className="block h-px bg-white origin-left"
    />
  </div>
);

const ModernMenu = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [activeGroup, setActiveGroup] = useState(navLinks[0]);
  const [mobileAccordionOpenGroupLabel, setMobileAccordionOpenGroupLabel] =
    useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      if (!open || !isMobile) {
        setMobileAccordionOpenGroupLabel(null);
      }
    })();
  }, [open, isMobile]);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .custom-nav-scroll::-webkit-scrollbar { width: 4px; }
        .custom-nav-scroll::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.02); }
        .custom-nav-scroll::-webkit-scrollbar-thumb { background: rgba(73, 145, 184, 0.2); border-radius: 10px; }
        .custom-nav-scroll::-webkit-scrollbar-thumb:hover { background: rgba(73, 145, 184, 0.5); }
      `,
        }}
      />
      <AnimatePresence mode="wait">
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 lg:p-8"
          >
            {/* Background Blur Overlay */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-xl"
              onClick={onClose}
            />

            {/* Floating Glass Container */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
              className="relative w-full h-full max-w-[1400px] overflow-hidden bg-white/5 backdrop-blur-3xl border border-white/10 rounded-4xl md:rounded-[3rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] flex flex-col"
            >
              <Noise className="opacity-[0.02]" />

              {/* ─── Header: only shown on DESKTOP ─── */}
              <div className="hidden lg:flex items-center justify-between px-14 py-8 border-b border-white/5 shrink-0 relative z-10">
                <div className="flex flex-col leading-none">
                  <span className="text-2xl font-black tracking-tighter text-white">
                    METAL<span className="text-[#4991b8] italic">FX</span>
                  </span>
                  <span className="text-[10px] font-bold tracking-[0.4em] text-zinc-500 uppercase mt-1">
                    Industrial Identity
                  </span>
                </div>
                <button
                  onClick={onClose}
                  className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:border-[#4991b8] hover:bg-[#4991b8]/10 transition-all duration-300 active:scale-90"
                >
                  <FaTimes size={14} />
                </button>
              </div>

              {/* ─── Mobile close button (no duplicate logo) ─── */}
              <div className="flex lg:hidden items-center justify-end px-4 py-3 border-b border-white/5 shrink-0 relative z-10">
                <button
                  onClick={onClose}
                  className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:border-[#4991b8] hover:bg-[#4991b8]/10 transition-all duration-300 active:scale-90"
                >
                  <FaTimes size={13} />
                </button>
              </div>

              {/* Content Area */}
              <div className="flex flex-1 overflow-hidden relative z-10 flex-col lg:flex-row">
                {/* ─── MOBILE: Full accordion, scrollable ─── */}
                <div
                  data-lenis-prevent
                  className="flex flex-col flex-1 overflow-y-auto custom-nav-scroll px-3 py-4 lg:hidden"
                >
                  {navLinks.map((group, idx) => (
                    <motion.div
                      key={group.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 + idx * 0.04 }}
                      className="mb-1"
                    >
                      {/* Accordion trigger */}
                      <button
                        onClick={() => {
                          if (group.dropdown) {
                            setMobileAccordionOpenGroupLabel(
                              mobileAccordionOpenGroupLabel === group.label
                                ? null
                                : group.label,
                            );
                          } else {
                            window.location.href = group.href;
                            onClose();
                          }
                        }}
                        className={`w-full text-left px-4 py-3.5 rounded-xl transition-all duration-300 flex items-center justify-between ${
                          mobileAccordionOpenGroupLabel === group.label
                            ? "bg-white/10 text-white"
                            : "text-white/50 hover:text-white/80 hover:bg-white/5"
                        }`}
                      >
                        <span className="text-sm font-black uppercase tracking-tight">
                          {group.label}
                        </span>
                        {group.dropdown ? (
                          <motion.span
                            animate={{
                              rotate:
                                mobileAccordionOpenGroupLabel === group.label
                                  ? 90
                                  : 0,
                            }}
                            transition={{ duration: 0.25 }}
                          >
                            <FaChevronRight
                              size={12}
                              className={
                                mobileAccordionOpenGroupLabel === group.label
                                  ? "text-[#4991b8]"
                                  : "text-white/30"
                              }
                            />
                          </motion.span>
                        ) : (
                          <FaArrowRight size={12} className="text-white/20" />
                        )}
                      </button>

                      {/* Accordion body */}
                      <AnimatePresence initial={false}>
                        {mobileAccordionOpenGroupLabel === group.label &&
                          group.dropdown && (
                            <motion.div
                              key="body"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{
                                duration: 0.3,
                                ease: [0.19, 1, 0.22, 1],
                              }}
                              className="overflow-hidden"
                            >
                              <div className="pl-4 pr-2 pb-3 pt-1 flex flex-col gap-1">
                                {group.dropdown.map((link, i) => (
                                  <motion.a
                                    key={link.label}
                                    href={link.href}
                                    initial={{ opacity: 0, x: -6 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.025 }}
                                    onClick={onClose}
                                    className="group flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                                  >
                                    <span className="text-xs font-semibold text-white/50 group-hover:text-white transition-colors uppercase tracking-wide">
                                      {link.label}
                                    </span>
                                    <FaChevronRight
                                      size={9}
                                      className="text-[#4991b8] opacity-0 group-hover:opacity-100 transition-opacity"
                                    />
                                  </motion.a>
                                ))}
                              </div>
                            </motion.div>
                          )}
                      </AnimatePresence>
                    </motion.div>
                  ))}

                  {/* Mobile phone numbers inside scroll area */}
                  <div className="mt-4 pt-4 border-t border-white/5 flex flex-col gap-3 px-2">
                    {phones.map((p) => (
                      <a
                        key={p.label}
                        href={`tel:${p.tel}`}
                        className="flex items-center gap-3 text-zinc-500 hover:text-[#4991b8] transition-colors group"
                      >
                        <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-[#4991b8]/20 transition-all shrink-0">
                          <FaPhoneAlt
                            size={8}
                            className="group-hover:text-[#4991b8]"
                          />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold tracking-widest uppercase opacity-60 whitespace-nowrap">
                            {p.label}
                          </span>
                          <span className="text-xs font-black text-white/80 whitespace-nowrap">
                            {p.number}
                          </span>
                        </div>
                      </a>
                    ))}

                    <CircleIconAnimationButton className="mt-2 w-full [&>button]:w-full">
                      Initialize Project
                    </CircleIconAnimationButton>
                  </div>
                </div>

                {/* ─── DESKTOP: Dual-pane layout ─── */}
                <div className="hidden lg:flex flex-1">
                  {/* Left Column */}
                  <div
                    data-lenis-prevent
                    className="w-1/3 border-r border-white/5 overflow-y-auto custom-nav-scroll shrink-0"
                  >
                    <div className="flex flex-col gap-2 p-14">
                      {navLinks.map((group, idx) => (
                        <motion.div
                          key={group.label}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + idx * 0.05 }}
                        >
                          <button
                            onMouseEnter={() => setActiveGroup(group)}
                            onClick={() => {
                              setActiveGroup(group);
                              if (!group.dropdown) {
                                window.location.href = group.href;
                                onClose();
                              }
                            }}
                            className={`group text-left w-full px-6 py-4 rounded-2xl transition-all duration-300 flex items-center justify-between ${
                              activeGroup.label === group.label
                                ? "bg-white/10 text-white lg:translate-x-3"
                                : "text-white/40 hover:text-white/80"
                            }`}
                          >
                            <span
                              className={`text-lg font-black uppercase tracking-tight transition-all ${
                                activeGroup.label === group.label
                                  ? "scale-105"
                                  : ""
                              }`}
                            >
                              {group.label}
                            </span>
                            <FaArrowRight
                              size={14}
                              className={`transition-all duration-300 ${
                                activeGroup.label === group.label
                                  ? "opacity-100 translate-x-0 text-[#4991b8]"
                                  : "opacity-0 -translate-x-4"
                              }`}
                            />
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div
                    data-lenis-prevent
                    className="flex-1 p-14 overflow-y-auto custom-nav-scroll relative"
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeGroup.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="relative z-10"
                      >
                        <h3 className="text-3xl font-black tracking-tighter text-white mb-6">
                          {activeGroup.label}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                          {activeGroup.dropdown ? (
                            activeGroup.dropdown.map((link, idx) => (
                              <motion.a
                                key={link.label}
                                href={link.href}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                onClick={onClose}
                                className="group flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
                              >
                                <span className="text-base font-bold text-white/70 group-hover:text-white transition-colors">
                                  {link.label}
                                </span>
                                <FaChevronRight
                                  size={12}
                                  className="ml-auto text-[#4991b8] opacity-0 group-hover:opacity-100 transition-opacity"
                                />
                              </motion.a>
                            ))
                          ) : (
                            <motion.a
                              href={activeGroup.href}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.05 }}
                              onClick={onClose}
                              className="group flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
                            >
                              <span className="text-base font-bold text-white/70 group-hover:text-white transition-colors">
                                View {activeGroup.label}
                              </span>
                              <FaChevronRight
                                size={12}
                                className="ml-auto text-[#4991b8] opacity-0 group-hover:opacity-100 transition-opacity"
                              />
                            </motion.a>
                          )}
                        </div>
                        <div className="text-[6rem] font-black text-white/3 leading-none pointer-events-none select-none mt-8">
                          METALFX
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
              <div className="hidden lg:flex items-center justify-between px-14 py-8 bg-black/20 border-t border-white/5 shrink-0 relative z-10">
                <div className="flex items-center gap-x-6">
                  {phones.map((p) => (
                    <a
                      key={p.label}
                      href={`tel:${p.tel}`}
                      className="flex items-center gap-2 text-zinc-500 hover:text-[#4991b8] transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-[#4991b8]/20 transition-all shrink-0">
                        <FaPhoneAlt
                          size={8}
                          className="group-hover:text-[#4991b8]"
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[8px] font-bold tracking-widest uppercase opacity-60 whitespace-nowrap">
                          {p.label}
                        </span>
                        <span className="text-xs font-black text-white/80 whitespace-nowrap">
                          {p.number}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
                <CircleIconAnimationButton>
                  Initialize Project
                </CircleIconAnimationButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;

      setScrolled(currentY > 20);

      if (diff > 5 && currentY > 200 && !menuOpen) {
        setHidden(true);
      } else if (diff < -5) {
        setHidden(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [menuOpen]);

  return (
    <>
      <motion.nav
        animate={{ y: hidden ? "-100%" : 0 }}
        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 px-2 md:px-8 ${
          scrolled || menuOpen ? "py-1.5 md:py-2.5" : "py-2.5 md:py-4"
        }`}
      >
        <div
          className={`max-w-[1600px] mx-auto px-10 flex items-center justify-between transition-all duration-500 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-full shadow-[0_20px_40px_rgba(0,0,0,0.1)] ${
            scrolled || menuOpen ? "py-1 md:py-1.5" : "py-1.5 md:py-2.5"
          }`}
        >
          {/* Logo */}
          <motion.a
            href="/"
            className="flex flex-col leading-none relative group"
            whileHover={{ scale: 1.02 }}
          >
            <span className="text-xl md:text-2xl font-black tracking-tighter text-white">
              METAL
              <span className="text-[#4991b8] italic transition-all duration-300 group-hover:text-white">
                FX
              </span>
            </span>
            <span
              className={`text-[8px] font-bold tracking-[0.2em] md:tracking-[0.4em] uppercase mt-1 transition-all duration-300 ${
                scrolled ? "text-zinc-500" : "text-zinc-400"
              }`}
            >
              Industrial Identity
            </span>
          </motion.a>

          {/* Right Action Bar */}
          <div className="flex items-center gap-4 md:gap-8">
            {/* Quick Contact — desktop only */}
            <div
              className={`hidden lg:flex items-center gap-6 transition-all duration-500 ${
                scrolled
                  ? "opacity-0 translate-x-10 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              {phones.slice(0, 1).map((p) => (
                <a
                  key={p.label}
                  href={`tel:${p.tel}`}
                  className="flex flex-col items-end group"
                >
                  <span className="text-[8px] font-bold tracking-widest text-[#4991b8] uppercase mb-0.5">
                    Direct Line
                  </span>
                  <span className="text-sm font-black text-white/80 group-hover:text-white transition-colors">
                    {p.number}
                  </span>
                </a>
              ))}
            </div>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="relative h-8 md:h-11 flex items-center gap-2 md:gap-4 pl-4 md:pl-5 pr-1 md:pr-4 transition-all duration-500 group"
            >
              <span className="hidden md:block text-[10px] font-black tracking-[0.2em] uppercase text-white/90 group-hover:text-white mt-0.5">
                {menuOpen ? "System Close" : "Navigation"}
              </span>
              <div
                className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                  menuOpen ? "text-[#4991b8]" : "text-white/70"
                }`}
              >
                <HamburgerIcon open={menuOpen} />
              </div>
            </button>
          </div>
        </div>
      </motion.nav>

      <ModernMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

export default Navbar;
